/**
 * A file's preview, drawn inline — the body of a Desktop viewer window (and
 * of the Mac Gallery view and Quick Look). What it can show comes from
 * `openerFor` (lib/file-openers.ts):
 *
 *   image     — fitted, with zoom and rotate
 *   pdf       — the browser's PDF viewer, from an inline-disposition URL
 *   document  — a Word document (.docx) converted to readable HTML
 *               (mammoth), sanitised before it is shown: the file may have
 *               come from anyone (an email attachment)
 *   text      — the first 100 KB, monospaced
 *   audio / video — native controls
 *
 * Anything else says there's no preview and offers the download.
 */
import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { AlertCircle, File as FileIcon, Loader2, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import { api, type VaultDocumentDto } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { FilePreview } from '../../lib/file-openers';

const TEXT_LIMIT = 100_000;
/** Word documents larger than this aren't converted in the browser. */
const DOCUMENT_PREVIEW_MAX_BYTES = 15 * 1024 * 1024;

/** What a converted Word document may contain — formatting and tables, data:
 *  images only, links to the web or mail. No scripts, styles or forms. */
const DOCUMENT_SANITIZE = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'sub',
    'sup',
    'a',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'table',
    'thead',
    'tbody',
    'tr',
    'td',
    'th',
    'img',
    'hr',
    'span',
  ],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'colspan', 'rowspan'],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|data:image\/(?:png|jpeg|gif|webp);)/i,
};

/**
 * A converted Word document, made safe to show: the allowlist above, then a
 * second pass of our own over the result — images only when embedded
 * (`data:`; a remote image would tell whoever wrote the document that it was
 * opened), links only to the web or mail and out to a new tab, and no event
 * handlers or script/style elements whatever the first pass left. The second
 * pass is belt and braces: it holds even where a DOM implementation walks the
 * tree differently (under happy-dom a link straight after a removed <script>
 * kept its `javascript:` href). Done on the sanitised fragment, not with a
 * DOMPurify hook: hooks are global and would change every other
 * sanitisation in the app.
 */
export function sanitizeDocumentHtml(html: string): string {
  const fragment = DOMPurify.sanitize(html, {
    ...DOCUMENT_SANITIZE,
    RETURN_DOM_FRAGMENT: true,
  }) as DocumentFragment;
  for (const el of Array.from(fragment.querySelectorAll('script, style, iframe, object, embed'))) {
    el.remove();
  }
  for (const el of Array.from(fragment.querySelectorAll('*'))) {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.toLowerCase().startsWith('on')) el.removeAttribute(attr.name);
    }
  }
  for (const img of Array.from(fragment.querySelectorAll('img'))) {
    if (!/^data:image\//i.test(img.getAttribute('src') ?? '')) img.remove();
  }
  for (const link of Array.from(fragment.querySelectorAll('a'))) {
    if (!/^(https?:|mailto:)/i.test((link.getAttribute('href') ?? '').trim())) {
      link.removeAttribute('href');
    }
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  }
  const holder = document.createElement('div');
  holder.appendChild(fragment);
  return holder.innerHTML;
}

export function FilePreviewPane({
  doc,
  preview,
  className,
  compact = false,
}: {
  doc: VaultDocumentDto;
  preview: FilePreview | null;
  className?: string;
  /** No zoom controls (Gallery / Quick Look thumbnails). */
  compact?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setText(null);
    setHtml(null);
    setError(null);
    setZoom(1);
    setRotation(0);
    if (!preview) return;
    setLoading(true);
    (async () => {
      try {
        const inline =
          preview === 'pdf' || preview === 'image' || preview === 'audio' || preview === 'video';
        const res = await api.getVaultDocumentDownloadUrl(doc.documentId, { inline });
        if (cancelled) return;
        if (preview === 'text') {
          const raw = await (await fetch(res.url)).text();
          if (!cancelled) setText(raw.slice(0, TEXT_LIMIT));
        } else if (preview === 'document') {
          if (doc.sizeBytes > DOCUMENT_PREVIEW_MAX_BYTES) {
            throw new Error('This document is too large to preview here.');
          }
          const buffer = await (await fetch(res.url)).arrayBuffer();
          const mammoth = await import('mammoth');
          const converted = await mammoth.convertToHtml({ arrayBuffer: buffer });
          if (!cancelled) setHtml(sanitizeDocumentHtml(converted.value));
        } else {
          setUrl(res.url);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Couldn’t load the preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doc.documentId, doc.sizeBytes, preview]);

  if (!preview) {
    return (
      <Centered className={className}>
        <FileIcon className="h-12 w-12 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">There’s no preview for this kind of file.</p>
      </Centered>
    );
  }
  if (error) {
    return (
      <Centered className={className}>
        <AlertCircle className="h-8 w-8 text-destructive" aria-hidden />
        <p className="max-w-sm text-center text-sm text-muted-foreground">{error}</p>
      </Centered>
    );
  }
  if (loading) {
    return (
      <Centered className={className}>
        <Loader2
          className="h-6 w-6 animate-spin text-muted-foreground"
          aria-label="Loading preview"
        />
      </Centered>
    );
  }

  if (preview === 'image' && url) {
    return (
      <div className={cn('relative flex h-full w-full flex-col bg-muted/30', className)}>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-3">
          <img
            src={url}
            alt={doc.name}
            draggable={false}
            className="max-h-full max-w-full object-contain transition-transform"
            style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }}
          />
        </div>
        {!compact && (
          <div className="absolute bottom-2 right-2 flex items-center gap-0.5 rounded-md border border-border bg-card/90 p-0.5 shadow-sm">
            <IconButton label="Zoom out" onClick={() => setZoom((z) => Math.max(0.25, z / 1.25))}>
              <ZoomOut className="h-4 w-4" />
            </IconButton>
            <button
              type="button"
              onClick={() => setZoom(1)}
              className="min-w-[3rem] rounded px-1 text-xs tabular-nums text-muted-foreground hover:bg-muted"
              title="Fit"
            >
              {Math.round(zoom * 100)}%
            </button>
            <IconButton label="Zoom in" onClick={() => setZoom((z) => Math.min(8, z * 1.25))}>
              <ZoomIn className="h-4 w-4" />
            </IconButton>
            <IconButton label="Rotate" onClick={() => setRotation((r) => (r + 90) % 360)}>
              <RotateCw className="h-4 w-4" />
            </IconButton>
          </div>
        )}
      </div>
    );
  }
  if (preview === 'pdf' && url) {
    return (
      <iframe
        src={url}
        title={`Preview of ${doc.name}`}
        className={cn('h-full w-full border-0 bg-muted/30', className)}
      />
    );
  }
  if (preview === 'video' && url) {
    return (
      <Centered className={cn('bg-black', className)}>
        <video src={url} controls className="max-h-full max-w-full" />
      </Centered>
    );
  }
  if (preview === 'audio' && url) {
    return (
      <Centered className={className}>
        <FileIcon className="h-12 w-12 text-muted-foreground" aria-hidden />
        <audio src={url} controls className="w-full max-w-md" />
      </Centered>
    );
  }
  if (preview === 'text' && text !== null) {
    return (
      <pre
        className={cn(
          'h-full w-full overflow-auto whitespace-pre-wrap break-words bg-background p-4 font-mono text-xs text-foreground',
          className,
        )}
      >
        {text}
      </pre>
    );
  }
  if (preview === 'document' && html !== null) {
    return (
      <div className={cn('h-full w-full overflow-auto bg-muted/40 p-4', className)}>
        <article
          className="prose prose-sm mx-auto max-w-3xl rounded bg-white p-8 text-black shadow-sm"
          // Sanitised by sanitizeDocumentHtml (allowlist, embedded images only).
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    );
  }
  return null;
}

function Centered({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn('flex h-full w-full flex-col items-center justify-center gap-3 p-4', className)}
    >
      {children}
    </div>
  );
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      {children}
    </button>
  );
}
